import { motion } from "motion/react";
import { useRouter } from "next/router";
import classNames from "classnames/bind";
import { forwardRef, useEffect, useMemo, useState } from "react";
import { AcfMediaFragment, SearchQuery } from "@graphqlTypes";
import { easeOutQuad } from "@/core/easing";
import { localizeURL, replaceStringVar, updateBodyScroll } from "@/core/utils";
import { useSearch } from "@/providers/SearchContext";
import { interleaveTrayResults, MergedResult } from "@/utils/interleaveResults";
import { Button, Image, TypePill, RhLink, YoutubeThumbnail } from "@components";
import { usePostType } from "@/hooks/postType";
import ArrowRight from "@/assets/svg/arrow-tail-right-no-padding.svg";

const cx = classNames.bind(styles);
import styles from "./Results.module.scss";
import { usePageWrapperData } from "@/providers/PageWrapperContext";
import { useHeader } from "@/providers/HeaderContext";

type Result = SearchQuery["search"]["nodes"][number];

const ResultImage = ({ image, youtubeId }: { image?: AcfMediaFragment; youtubeId?: string }) => {
  if (image) {
    return <Image image={image} />;
  } if (youtubeId) {
    return <YoutubeThumbnail youtubeId={youtubeId} />;
  }
  return null;
};

const Result = forwardRef<HTMLAnchorElement, Result & { pressReleaseSearchImage: AcfMediaFragment }>((props, ref) => {
  const {
    title,
    uri,
    __typename,
    pageData,
  } = props;
  const resultType = usePostType({ node: { ...props, __typename } });
  const resultTitle = useMemo(() => {
    if (__typename === "RhtvVideo") {
      const { rhtvVideoContent: { videoTitle } = {} } = props;
      return videoTitle || title;
    }
    return title;
  }, []);
  const { siteSettings: { pressReleaseSearchImage } = {} } = usePageWrapperData();
  const { currentLanguage } = useHeader();
  const url = useMemo(() => localizeURL(uri, currentLanguage), [uri, __typename]);

  const image = useMemo(() => {
    if (__typename === "RhtvVideo") {
      return props.rhtvVideoContent?.thumbnail || null;
    } else if (__typename === "RhtvChannel") {
      const { rhtvChannelContent: { thumbnail } } = props;
      return thumbnail;
    } else if (__typename === "PressRelease") {
      return pressReleaseSearchImage;
    }
    return pageData?.featuredImage;
  }, [__typename, props]);

  const youtubeId = useMemo(() => {
    if (__typename === "RhtvVideo") return props.rhtvVideoContent?.youtubeVideoId || null;
    return null;
  }, [__typename]);
  
  if (!url) return null;

  return (
    <RhLink className={cx("search-result")} ref={ref} href={url} role="option">
      <div className={cx("search-result__image")}>
        <ResultImage image={image} youtubeId={youtubeId} />
      </div>
      <div className={cx("search-result__content")}>
        <div className={cx("copy")}>
          {resultType && (
            <TypePill
              className={cx("search-result__type")}
              type={resultType.key}
              copy={resultType.label}
            />
          )}
          <p className={cx(["title", "title-lc-xs"])}>{resultTitle}</p>
        </div>
        <ArrowRight className={cx("arrow")} />
      </div>
    </RhLink>
  );
});

const MotionResult = motion.create(Result);

const TrayShopResult = forwardRef<HTMLAnchorElement, ShopifyCarouselProduct>((product, ref) => {
  const { title, onlineStoreUrl, featuredImage, priceRange } = product;
  const { labels: { postTypeLabels: { shop } = { } } = {} } = usePageWrapperData();
  const price = useMemo(() => {
    const min = parseFloat(priceRange.minVariantPrice.amount);
    const currency = priceRange.minVariantPrice.currencyCode || "USD";
    return new Intl.NumberFormat("en-US", { style: "currency", currency, minimumFractionDigits: 2 }).format(min);
  }, [priceRange]);

  if (!onlineStoreUrl) return null;

  return (
    <a className={cx("search-result", "search-result--shop")} ref={ref} href={onlineStoreUrl} target="_blank" rel="noopener noreferrer" role="option">
      <div className={cx("search-result__image")}>
        {featuredImage?.url && (
          <img src={featuredImage.url} alt={title} loading="lazy" />
        )}
      </div>
      <div className={cx("search-result__content")}>
        <div className={cx("copy")}>
          <TypePill className={cx("search-result__type")} type="shop" copy={shop || "Shop"} />
          <p className={cx(["title", "title-lc-xs"])}>{title}</p>
          <p className={cx("shop-price")}>{price}</p>
        </div>
        <ArrowRight className={cx("arrow")} />
      </div>
    </a>
  );
});

const MotionTrayShopResult = motion.create(TrayShopResult);

const SearchTrayResults = forwardRef<HTMLDivElement, { resultsCtaLabel: string; pressReleaseSearchImage: AcfMediaFragment }>(({ resultsCtaLabel, pressReleaseSearchImage }, ref) => {
  const { autoCompleteResults, searchPath, setGoingToSearch, setIgnoreSearch, resultsCount, lastSearchTerm, autoCompleteLoading, shopResults } = useSearch();
  const [innerResults, setInnerResults] = useState<MergedResult[]>([]);
  const delayIncrement = 0.085;
  const duration = 0.3;
  const router = useRouter();
  const totalCount = resultsCount + shopResults.length;
  const ctaCopy = replaceStringVar(resultsCtaLabel, { count: totalCount.toString() });

  const handleGoToSearch = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const searchUrl = `${searchPath}?s=${encodeURIComponent(lastSearchTerm)}`;
    setIgnoreSearch(true);
    setGoingToSearch(true);
    router.push(searchUrl);
    updateBodyScroll(false);
  }
  useEffect(() => {
    setInnerResults(interleaveTrayResults(autoCompleteResults, shopResults));
  }, [autoCompleteResults, shopResults]);
  return (
    <div className={cx(["search-results"])} ref={ref}>
      <div className={cx("search-results__content", { loading: autoCompleteLoading })}>
        {innerResults.map((item, index) =>
          item.type === 'wp' ? (
            <MotionResult
              initial={{ opacity: 0, y: 20 }}
              animate={{
                opacity: 1,
                y: 0,
                transition: {
                  delay: index * delayIncrement,
                  ease: easeOutQuad,
                  duration,
                },
              }}
              exit={{ opacity: 0, transition: { duration, ease: easeOutQuad } }}
              pressReleaseSearchImage={pressReleaseSearchImage}
              key={item.data.databaseId}
              {...item.data}
            />
          ) : (
            <MotionTrayShopResult
              initial={{ opacity: 0, y: 20 }}
              animate={{
                opacity: 1,
                y: 0,
                transition: {
                  delay: index * delayIncrement,
                  ease: easeOutQuad,
                  duration,
                },
              }}
              exit={{ opacity: 0, transition: { duration, ease: easeOutQuad } }}
              key={`shop-${item.data.onlineStoreUrl}`}
              {...item.data}
            />
          )
        )}
        { (resultsCount > 0 || innerResults.length > 0) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{
              opacity: 1,
              y: 0,
              transition: {
                delay: innerResults.length * delayIncrement,
                ease: easeOutQuad,
                duration,
              },
            }}
            exit={{ opacity: 0 }}
            transition={{ duration, ease: easeOutQuad }}
            className={cx("search-results__button-row")}
          >
            <Button
              label={ctaCopy}
              onClick={handleGoToSearch}
            />
          </motion.div>
        )}
      </div>
    </div>
  );
});

export default motion.create(SearchTrayResults);
